import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageViewer from 'react-simple-image-viewer';
import { useLocation } from 'react-use';

import { Pill } from '@shared/ui/Pill';
import { getYearsForText } from '@shared/utils';
import { transliterate } from '@shared/utils/strings';
import { Routes } from '@shared/enums';

import { useLanguage } from '@hooks/useLanguage';

import { ShowPictureButton } from './ShowPictureButton';

interface SantaLetterApplicationCardItemDetailsProps {
  details: Record<string, unknown>;
}

function useImageViewer() {
  const [isImageVisible, setIsImageVisible] = useState(false);

  return {
    isVisible: isImageVisible,
    showImage: () => setIsImageVisible(true),
    hideImage: () => setIsImageVisible(false),
  };
}

function useComponentState({ details }: SantaLetterApplicationCardItemDetailsProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  const location = useLocation();

  const { isVisible, showImage, hideImage } = useImageViewer();

  const { name, age, text, translatedText, picture } = details;
  const { isChildWithDisabilities, isChildFromLargeFamily, isChildOrphan } = details;
  const isHasAdditionalConditions = isChildWithDisabilities || isChildFromLargeFamily || isChildOrphan;

  const header = useMemo(
    () => `${transliterate(String(name), language)}, ${age} ${t(getYearsForText(age as number))}`,
    [language],
  );

  /**
   * NOTE: Temporary strange feature = strange solution
   */
  const isLetterTextVisible = location.pathname?.includes(Routes.CustomerCabinet) || false;

  return {
    header,
    picture: String(picture),

    isHasAdditionalConditions,
    isChildWithDisabilities,
    isChildFromLargeFamily,
    isChildOrphan,

    letterText: isLetterTextVisible ? (language === 'uk' ? text : translatedText) : null,

    isImageVisible: !!picture && isVisible,
    showImage,
    hideImage,
  } as const;
}

export const SantaLetterApplicationCardItemDetails: React.FC<SantaLetterApplicationCardItemDetailsProps> = (props) => {
  const {
    header,
    picture,
    letterText,

    isHasAdditionalConditions,
    isChildOrphan,
    isChildFromLargeFamily,
    isChildWithDisabilities,

    isImageVisible,
    showImage,
    hideImage,
  } = useComponentState(props);
  const { t } = useTranslation();

  return (
    <div className="flex flex-col space-y-4 items-start md:flex-row md:space-y-0 md:space-x-4 text-xs">
      <div className="mr-auto">
        <p className="mt-2 mb-4">{header}</p>

        {isHasAdditionalConditions && (
          <div className="flex gap-2 mt-2 whitespace-nowrap">
            {isChildWithDisabilities && <Pill>{t('ch-disab')}</Pill>}
            {isChildFromLargeFamily && <Pill>{t('ch-large')}</Pill>}
            {isChildOrphan && <Pill>{t('ch-orphan')}</Pill>}
          </div>
        )}

        {letterText && <p className="mt-4">{letterText}</p>}
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        {isImageVisible && (
          <ImageViewer
            src={[picture]}
            currentIndex={0}
            closeOnClickOutside
            disableScroll
            backgroundStyle={{ zIndex: 9999, marginLeft: 0, marginTop: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
            onClose={hideImage}
          />
        )}
      </div>

      {picture && <ShowPictureButton url={picture} onClick={showImage} />}
    </div>
  );
};
